// League Rules -- the official format and rules of the NIKAO CUP.
//
// The five numbered rules are the league's own wording, kept verbatim so
// there is a single authoritative copy players can point at. Everything in
// a grey "how the app handles this" box is an implementation note added by
// the app, clearly separated from the rules themselves.
//
// Two rules are enforced in code (see src/utils/handicap.js):
//   Rule 2  85% allowance, 18.0 index cap, 8-stroke match ceiling
//   Rule 4  tee tiers, locked for the season on the Roster tab
//
// One rule is deliberately NOT in code: the hole 9 re-rating. The app does
// not track hole-by-hole play, so players apply it on the scorecard. It is
// spelled out below with a worked table.

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ScrollText, Info } from 'lucide-react';
import {
  MAX_HANDICAP_INDEX, MATCH_HANDICAP_ALLOWANCE, MAX_STROKES_PER_MATCH,
} from '../utils/handicap';

const ALLOWANCE_PCT = Math.round(MATCH_HANDICAP_ALLOWANCE * 100);

// Hole 9 drops out, so every other hole moves up one rating.
// Original stroke-index rating -> league rating for holes 1-8.
const RERATING = [
  { original: 1, league: 1, note: 'unchanged - already the hardest' },
  { original: 2, league: 1, note: 'was No. 2, becomes No. 1' },
  { original: 3, league: 2, note: 'was No. 3, becomes No. 2' },
  { original: 4, league: 3, note: null },
  { original: 5, league: 4, note: null },
  { original: 6, league: 5, note: null },
  { original: 7, league: 6, note: null },
  { original: 8, league: 7, note: null },
  { original: 9, league: null, note: 'hole 9 - not a stroke hole' },
];

function Note({ children }) {
  return (
    <div className="mt-3 flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Rule({ n, children }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-sm font-bold text-white">
        {n}
      </span>
      <div className="min-w-0 flex-1 pt-0.5">{children}</div>
    </li>
  );
}

export default function RulesTab() {
  return (
    <div className="mt-4 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ScrollText className="h-5 w-5" />Format and Rules
          </CardTitle>
          <CardDescription>
            The official format and rules of the NIKAO CUP. Where the app enforces a rule
            automatically, that is called out beneath it.
          </CardDescription>
        </CardHeader>

        <CardContent>
          <ol className="space-y-6">
            {/* ---------------------------------------------------------- */}
            <Rule n={1}>
              <p className="text-gray-900">All matches are 9 holes.</p>
            </Rule>

            {/* ---------------------------------------------------------- */}
            <Rule n={2}>
              <p className="text-gray-900">
                We use <strong>{ALLOWANCE_PCT}% of your handicap</strong> to determine strokes
                (<strong>max strokes per match {MAX_STROKES_PER_MATCH}</strong>). We also agreed in
                our post meeting last year that <strong>hole 9 will not be considered a handicap
                hole</strong>. The reason &mdash; hate for a match to come down to the last hole
                determined by strokes, so holes 1&ndash;8 will be used.
              </p>
              <p className="mt-2 text-gray-900">
                Example: hole 8, currently rated No.&nbsp;2, becomes the No.&nbsp;1 rated hole;
                hole 7, currently rated No.&nbsp;3, becomes No.&nbsp;2 rated; and so on.
              </p>

              <div className="mt-3 overflow-x-auto">
                <table className="text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
                      <th className="py-2 pr-6">Card rating</th>
                      <th className="py-2 pr-6">League rating</th>
                      <th className="py-2">&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RERATING.map(r => (
                      <tr
                        key={r.original}
                        className={`border-b last:border-0 ${r.league === null ? 'bg-amber-50' : ''}`}
                      >
                        <td className="py-1.5 pr-6 tabular-nums text-gray-700">No. {r.original}</td>
                        <td className="py-1.5 pr-6 font-medium tabular-nums text-gray-900">
                          {r.league === null
                            ? <span className="text-amber-800">no stroke</span>
                            : `No. ${r.league}`}
                        </td>
                        <td className="py-1.5 text-xs text-gray-500">{r.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Note>
                <p>
                  <strong>How the app handles this.</strong> Strokes are computed as{' '}
                  {ALLOWANCE_PCT}% of each player&rsquo;s 9-hole course handicap, with a handicap
                  index cap of {MAX_HANDICAP_INDEX}.0 and a hard ceiling of{' '}
                  {MAX_STROKES_PER_MATCH} strokes per match. The tee sheet and results pages show
                  the exact number for every match.
                </p>
                <p>
                  The hole 9 re-rating above is <strong>applied by the players on the scorecard</strong>{' '}
                  &mdash; the app does not track hole-by-hole play. The {MAX_STROKES_PER_MATCH}-stroke
                  ceiling exists because only 8 of the 9 holes are stroke holes.
                </p>
              </Note>
            </Rule>

            {/* ---------------------------------------------------------- */}
            <Rule n={3}>
              <p className="text-gray-900">
                <strong>Play the ball down.</strong> If in a fairway divot, notify your playing
                partner about relief (a couple of inches sideways or backwards preferred). If in an
                unmaintained bunker, notify your playing partner about relief (rake and place).
              </p>
              <p className="mt-2 text-gray-900">
                Again &mdash; we are here to compete, but this is intended to be both fun and fair.
              </p>
              <p className="mt-2 text-gray-900">
                We will go off in <strong>foursomes, 2 matches at once</strong>.
              </p>
            </Rule>

            {/* ---------------------------------------------------------- */}
            <Rule n={4}>
              <p className="text-gray-900">
                <strong>White tees will be used unless 60 or older.</strong> If 60 or older you must
                declare, prior to the start of the league, which tees you will play from for the
                entirety of the league.
              </p>
              <Note>
                <p>
                  <strong>How the app handles this.</strong> A player&rsquo;s tee is set once on the
                  Roster tab as a <em>tee tier</em>, which resolves to the right colour at each
                  course &mdash; tier 1 is White at Champions Pointe and Black at Covered Bridge;
                  tier 2 is Gray and Gold. Everyone starts on tier 1.
                </p>
                <p>
                  Cross-tee matches are handled correctly: the course handicap formula includes the
                  tee-box rating adjustment, so a White player and a Gray player are compared fairly.
                </p>
              </Note>
            </Rule>

            {/* ---------------------------------------------------------- */}
            <Rule n={5}>
              <p className="text-gray-900">
                Pre-league divisions will be determined by handicap index, to equally divide into
                4 divisions:
              </p>
              <p className="mt-2 font-semibold tracking-wide text-gray-900">
                NICKLAUS &middot; WOODS &middot; PALMER &middot; HOGAN
              </p>
            </Rule>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Scoring &amp; playoffs</CardTitle>
          <CardDescription>
            Not part of the format rules above, but worth having in one place.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-gray-700">
          <p>
            <strong>Points.</strong> Win 5 &middot; Tie 3 &middot; Loss 1 &middot; Forfeit 0 (the
            opponent of a forfeit receives 3).
          </p>
          <p>
            <strong>Tiebreakers, in order.</strong> Head-to-head result, then most wins, then
            combined points from wins and ties, then a chip-off or 40-yard dash recorded by the
            commissioner.
          </p>
          <p>
            <strong>Playoffs.</strong> The top 4 from each division reach a 16-player
            single-elimination bracket. A halved playoff match cannot stand &mdash; play extra holes
            and record a winner.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
